import type { IngredientId } from '../brandedTypes/ingredientId';
import type {
  Dictionary,
  ContentApiImage,
  Money,
  StaticInfoType,
} from '../general';

import type { GuestRatingBreakdown, HotelAmenities } from './getHotels';

export interface ProductStaticInfoMap {
  [StaticInfoType.Overview]: OverviewHotel;
  [StaticInfoType.OverviewActivity]: OverviewActivity;
  [StaticInfoType.OverviewHeartedHotel]: OverviewHeartedHotel;
  [StaticInfoType.Destination]: DestinationHotel;
  [StaticInfoType.Details]: DetailsHotel;
  [StaticInfoType.OverviewHotelsAmount]: OverviewHotelsAmountOnly;
  [StaticInfoType.NameOnly]: NameOnlyHotel;
  [StaticInfoType.MainPolygonOnly]: MainPolygonOnlyHotel;
  [StaticInfoType.PositionOnly]: PositionOnlyHotel;
  [StaticInfoType.ActivitySuggestions]: ActivityStaticInfo;
}

export interface OverviewHotel {
  hotelCode: string;
  name: string;
  address: Address;
  images: ContentApiImage[];
  semboSunRating: FullStars;
  position: Position;
  typeOfHotel: AccommodationType;
  mainPolygon?: MainPolygon;
}

export interface OverviewActivity {
  hotelCode: string;
  name: string;
  images: ContentApiImage[];
  semboSunRating: FullStars;
  typeOfHotel: AccommodationType;
  mainPolygon?: MainPolygon;
}

export interface OverviewHeartedHotel extends OverviewHotel {
  polygonIds: number[];
}

export interface DestinationHotel {
  hotelCode: string;
  name: string;
  images: ContentApiImage[];
  semboSunRating: FullStars;
  position: Position;
  popularityScore: number;
  polygonIds: number[];
  typeOfHotel: AccommodationType;
  mainPolygon?: MainPolygon;
  address?: Address;
  openingParagraph?: OpeningParagraph;
  amenities?: HotelAmenities;
  guestRatings?: GuestRatingBreakdown;
  rooms?: HotelRoom[];
}

export interface HotelRoom {
  boardBasis?: string;
}

export interface DetailsHotel {
  hotelCode: string;
  name: string;
  description: Description;
  openingParagraph: OpeningParagraph;
  address: Address;
  images: ContentApiImage[];
  properties: Dictionary<ValueWithDescription>;
  semboSunRating: FullStars;
  position: Position;
  distances: Dictionary<ValueWithDescription<number>>;
  polygonIds: number[];
  mainPolygon: MainPolygon;
  typeOfHotel: AccommodationType;
  tags: Tag[];
  amenities?: HotelAmenities;
}

export interface OverviewHotelsAmountOnly {
  totalNumberOfHotelsFound: number;
}

export interface NameOnlyHotel {
  name: string;
  hotelCode: string;
}

export interface MainPolygonOnlyHotel {
  mainPolygon?: {
    fullName: string;
    id: number;
  };
}

export interface PositionOnlyHotel {
  position: Position;
}

export interface ActivitySuggestionsResponse {
  suggestions: ActivitySuggestion[];
  ingredientIndex: number;
}

export interface ActivitySuggestion {
  code: string;
  price: Money | null;
}

export interface ActivitySuggestionWithStaticInfo extends ActivitySuggestion {
  name: string;
  images: ContentApiImage[];
}

export type ActivitySuggestionsByIngredientId = Record<
  IngredientId,
  ActivitySuggestionWithStaticInfo[]
>;

export interface ActivityStaticInfo {
  hotelCode: string;
  name: string;
  images: ContentApiImage[];
}

export interface Description {
  twoLetterIsoLanguageCode: string | null;
  value: string | null;
}

interface OpeningParagraph {
  twoLetterIsoLanguageCode: string;
  value: string;
}

export interface Address {
  city: string;
  country: string;
}

interface ValueWithDescription<T = unknown> {
  description: string;
  value: T;
}

interface Position {
  longitude: ServerCoordinate;
  latitude: ServerCoordinate;
}

interface ServerCoordinate {
  decimalDegrees: number;
}

export const enum AccommodationRule {
  FreeCancellation = 'FreeCancellation',
}

export enum AccommodationType {
  Hotel = 'Hotel',
  Apartment = 'Apartment',
  HouseOrCottage = 'HouseOrCottage',
  Ticket = 'Ticket',
  Golf = 'Golf',
}

export type AccommodationProductType =
  | AccommodationType.Hotel
  | AccommodationType.Apartment
  | AccommodationType.HouseOrCottage;

export type ActivityProductType =
  | AccommodationType.Ticket
  | AccommodationType.Golf;

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export type StarsRatingFilter = FullStars | 0;

export const enum AmenitiesFilter {
  Pool = 'Pool',
  Parking = 'Parking',
  Spa = 'Spa',
  Gym = 'Gym',
  Wifi = 'Wifi',
  AirConditioned = 'AirConditioned',
  VehicleChargingStation = 'VehicleChargingStation',
}

export const enum ExperienceFilter {
  Business = 'Business',
  Family = 'Family',
  PetsAllowed = 'PetsAllowed',
}

export enum BoardBasisFilter {
  RoomOnly = 'RoomOnly',
  Breakfast = 'Breakfast',
  HalfBoard = 'HalfBoard',
  FullBoard = 'FullBoard',
  AllInclusive = 'AllInclusive',
}

export enum BoardBasisCode {
  RO = 'RO',
  BB = 'BB',
  HB = 'HB',
  'HB+' = 'HB+',
  FB = 'FB',
  AI = 'AI',
}

export const HOTEL_RULES: AccommodationRule[] = [
  AccommodationRule.FreeCancellation,
];

export const HOTEL_TYPES: AccommodationType[] = [
  AccommodationType.Hotel,
  AccommodationType.Apartment,
  AccommodationType.HouseOrCottage,
];

export const OLD_POPULAR_FILTERS = [
  AmenitiesFilter.Pool,
  AmenitiesFilter.Parking,
  ExperienceFilter.PetsAllowed,
];

export const AMENITIES_FILTERS: AmenitiesFilter[] = [
  AmenitiesFilter.Pool,
  AmenitiesFilter.Parking,
  AmenitiesFilter.Spa,
  AmenitiesFilter.Gym,
  AmenitiesFilter.Wifi,
  AmenitiesFilter.AirConditioned,
  AmenitiesFilter.VehicleChargingStation,
];

export const BOARD_BASIS_FILTERS: BoardBasisFilter[] = [
  BoardBasisFilter.RoomOnly,
  BoardBasisFilter.Breakfast,
  BoardBasisFilter.HalfBoard,
  BoardBasisFilter.FullBoard,
  BoardBasisFilter.AllInclusive,
];

export const EXPERIENCE_FILTERS: ExperienceFilter[] = [
  ExperienceFilter.Business,
  ExperienceFilter.Family,
  ExperienceFilter.PetsAllowed,
];

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const FULL_STAR_LIST = [1, 2, 3, 4, 5] as const;

export type FullStars = (typeof FULL_STAR_LIST)[number];

export interface Tag {
  name: string;
}

export interface MainPolygon {
  id: number;
  fullName: string;
}

export interface HeartedHotel {
  code: string;
  roomCodes?: string[];
  isRefundable?: boolean;
  rooms?: HeartedRoom[];
}

export interface HeartedRoom {
  code: string;
  boardBasis: string;
  roomTypeId?: number;
}
